import type { Element as XmlEl } from "@xmpp/xml"
import type {
  ChatStateNotice,
  EditMessage,
  Message,
  RetractionMessage,
} from "../../types"
import { isCarbon, isCarbonBy } from "./carbons"
import { isBouncedChatState, parseChatState } from "./chatstate"
import { isErrorMessage, readMessageBounce, type MessageBounce } from "./error"
import { isMamResult } from "./mam"
import type { Jid } from "../jid"
import { parse } from "./message"

export type StanzaHandlers = {
  onMessage: (message: Message) => void
  onRetraction: (retraction: RetractionMessage) => void
  onEdit: (edit: EditMessage) => void
  onBounce: (bounce: MessageBounce) => void
  onChatState: (notice: ChatStateNotice) => void
}

/**
 * Route one inbound stanza.
 *
 * Everything arrives on a single event, so the shape is a filter chain rather
 * than one listener per kind: not a message, a MAM result, a bounce, a forged
 * carbon, a chat state, a retraction or an edit, then an ordinary message.
 */
export const routeStanza = (
  stanza: XmlEl,
  selfBareJid: Jid,
  handlers: StanzaHandlers
): void => {
  if (!stanza.is("message")) return

  // Collected by the query that asked for them, not replayed as live traffic.
  if (isMamResult(stanza)) return

  if (isErrorMessage(stanza)) {
    if (isBouncedChatState(stanza)) return

    const bounce = readMessageBounce(stanza)
    if (bounce) handlers.onBounce(bounce)
    return
  }

  // Before parsing, because the parser discards the envelope this check needs.
  // The endpoint takes raw XMPP from anyone holding a session token, so an
  // unchecked carbon renders as a message we wrote, in a conversation they
  // chose. The envelope's `from` is the one thing they cannot forge.
  if (isCarbon(stanza) && !isCarbonBy(stanza, selfBareJid)) {
    console.warn("[chat] dropping a carbon we did not send:", stanza.toString())
    return
  }

  // Before parsing too: the parser takes anything bodyless for an empty message.
  const chatState = parseChatState(stanza)
  if (chatState) {
    handlers.onChatState(chatState)
    return
  }

  const message = parse(stanza)
  if (!message) return

  if (message.type === "retraction") handlers.onRetraction(message)
  else if (message.type === "edit") handlers.onEdit(message)
  else handlers.onMessage(message)
}
